import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { SignInOutput } from 'aws-amplify/auth'
import {
  confirmNewPasswordChallenge,
  confirmTotpChallenge,
  getAuthUser,
  keepAliveAuthSession,
  signInWithEmail,
  signInWithHostedUi,
  signOutUser,
  type AuthUser,
} from '../services/auth-service'
import { isCognitoConfigured } from '../config/cognito'
import { whenAuthStorageReady } from '../config/amplify'
import { setCallFlowUser } from '../debug/call-flow-log'
import {
  checkSessionExpiry,
  clearSessionTracker,
  hasSessionTracker,
  markSessionExpired,
  recordSessionStart,
  touchSessionActivity,
} from '../utils/session-tracker'

type AuthContextValue = {
  user: AuthUser | null
  isLoading: boolean
  isConfigured: boolean
  signInHosted: () => Promise<void>
  signInEmail: (email: string, password: string) => Promise<SignInOutput>
  confirmNewPassword: (newPassword: string) => Promise<SignInOutput>
  /** Answer the Cognito TOTP challenge for accounts with an authenticator. */
  confirmTotpCode: (code: string) => Promise<SignInOutput>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isConfigured = isCognitoConfigured()

  // Attribute call-flow telemetry to the current user (call+audio state only —
  // never touches auth config). Cleared on sign-out.
  useEffect(() => {
    setCallFlowUser(user?.userId ?? null)
  }, [user])

  const refresh = useCallback(async () => {
    try {
      const next = await getAuthUser()
      if (next) {
        if (!hasSessionTracker()) recordSessionStart()
        else touchSessionActivity()
      }
      setUser(next)
    } catch (err) {
      // Preserve the existing session on transient network failures.
      console.warn('[auth] Session refresh skipped (transient error)', err)
    }
  }, [])

  const expireSession = useCallback(async (reason: 'idle' | 'absolute') => {
    markSessionExpired(reason)
    clearSessionTracker()
    try {
      await signOutUser()
    } catch {
      /* best-effort — the local expiry below is unconditional */
    }
    // Expiry is a definitive local teardown (the user is dropped regardless of
    // whether Cognito was reachable), so the soft-lock clocks clear alongside
    // it — same invariant as signOut(): never before the session is torn down.
    try {
      const { clearSessionSecurityClocks } = await import('../services/biometric-auth-service')
      clearSessionSecurityClocks()
    } catch {
      /* non-fatal */
    }
    // Even when signOutUser() threw part-way, an expired session must not leave
    // the previous account's test-recorder audio on a shared device.
    try {
      const recorder = await import('../services/session-recorder')
      await recorder.wipeSessionRecorderTestData()
    } catch {
      /* best-effort — the native 24 h sweep is the backstop */
    }
    setUser(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!isConfigured) {
        setIsLoading(false)
        return
      }
      // Native: ensure Preferences-backed token storage + migration finished
      // before the first session read, otherwise we'd see an empty store and
      // bounce the user to sign-in on cold start.
      await whenAuthStorageReady()
      let next: AuthUser | null = null
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          next = await getAuthUser()
          break
        } catch {
          if (attempt < 2) {
            await new Promise((resolve) => setTimeout(resolve, 750 * (attempt + 1)))
          }
        }
      }
      if (!cancelled) {
        if (next) {
          if (!hasSessionTracker()) recordSessionStart()
        }
        setUser(next)
        setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isConfigured])

  useEffect(() => {
    if (!isConfigured) return

    const refreshProfile = () => {
      void refresh()
    }

    const evaluateExpiry = () => {
      if (!user) return
      const reason = checkSessionExpiry()
      if (reason) void expireSession(reason)
    }

    const onActivity = () => {
      if (user) touchSessionActivity()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      evaluateExpiry()
      if (!user) return
      void (async () => {
        try {
          await keepAliveAuthSession()
          await refresh()
        } catch (err) {
          console.warn('[auth] Foreground keep-alive skipped (transient error)', err)
        }
      })()
    }

    window.addEventListener('focus', refreshProfile)
    window.addEventListener('pointerdown', onActivity, { passive: true })
    window.addEventListener('keydown', onActivity)
    document.addEventListener('visibilitychange', onVisibilityChange)
    const interval = setInterval(() => {
      evaluateExpiry()
      refreshProfile()
    }, 5 * 60_000)

    return () => {
      window.removeEventListener('focus', refreshProfile)
      window.removeEventListener('pointerdown', onActivity)
      window.removeEventListener('keydown', onActivity)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearInterval(interval)
    }
  }, [isConfigured, refresh, user, expireSession])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isConfigured,
      signInEmail: async (email, password) => {
        const result = await signInWithEmail(email, password)
        if (result.isSignedIn) {
          recordSessionStart()
          await refresh()
        }
        return result
      },
      confirmNewPassword: async (newPassword) => {
        const result = await confirmNewPasswordChallenge(newPassword)
        if (result.isSignedIn) {
          recordSessionStart()
          await refresh()
        }
        return result
      },
      confirmTotpCode: async (code) => {
        const result = await confirmTotpChallenge(code)
        if (result.isSignedIn) {
          recordSessionStart()
          await refresh()
        }
        return result
      },
      signInHosted: async () => {
        await signInWithHostedUi()
        recordSessionStart()
        await refresh()
      },
      signOut: async () => {
        // Best-effort explicit OFFLINE before tokens are cleared — readers
        // would otherwise keep showing IDLE until the presence decays.
        try {
          const { updatePresence } = await import('../services/appsync-client')
          await updatePresence('OFFLINE')
        } catch {
          /* presence is best-effort */
        }
        // Fail-closed: tear the session down FIRST. If signOutUser() throws,
        // the user is still signed in, so the soft-lock clocks must stay
        // armed — clearing them here would leave the surviving session
        // unlocked without verification (12h re-verify policy).
        await signOutUser()
        // 20A: session/tokens are confirmed cleared — now clear the
        // per-device soft-lock clocks so the next login re-verifies.
        try {
          const { clearSessionSecurityClocks } = await import(
            '../services/biometric-auth-service'
          )
          clearSessionSecurityClocks()
        } catch {
          /* non-fatal */
        }
        clearSessionTracker()
        setUser(null)
      },
      refresh,
    }),
    [user, isLoading, isConfigured, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/**
 * Read-only variant for hooks that can render outside AuthProvider (tests,
 * storybook-style harnesses). Returns null instead of throwing.
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext)
}
